import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MessageCircle, AlertTriangle } from "lucide-react";
import MessageCard from "@/components/MessageCard";
import SeoHead from "@/components/SeoHead";
import { useLang } from "@/lib/useLang";
import { getOrCreateVoterId } from "@/lib/voter";
import Analytics from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const UPVOTED_KEY = "kerala_speaks_upvoted";

function loadUpvotedSet() {
  try {
    const raw = localStorage.getItem(UPVOTED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
}

/**
 * Deep-link landing page for a single Kerala Speaks message.
 * Route: /m/:id
 *
 * Friend lands directly on:
 *   - the message  - prominent upvote CTA
 *   - related messages from the same category
 *   - link back to the full feed
 */
export const MessageDetail = () => {
  const { t, lang } = useLang();
  const { id } = useParams();
  const navigate = useNavigate();
  const [voterId] = useState(getOrCreateVoterId);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [upvotedSet, setUpvotedSet] = useState(() => loadUpvotedSet());

  useEffect(() => {
    setLoading(true);
    setError(false);
    axios.get(`${API}/messages/${id}`).then((r) => {
      setData(r.data);
    }).catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    axios.get(`${API}/categories`).then((r) => setCategories(r.data?.categories || [])).catch(() => {});
    axios.get(`${API}/upvoted-by/${voterId}`).then((r) => {
      const ids = r.data?.message_ids || [];
      if (ids.length) {
        setUpvotedSet((prev) => {
          const next = new Set(prev);
          ids.forEach((m) => next.add(m));
          try { localStorage.setItem(UPVOTED_KEY, JSON.stringify([...next].slice(-2000))); } catch { /* ignore */ }
          return next;
        });
      }
    }).catch(() => {});
  }, [voterId]);

  const categoryName = (cid) => {
    const c = categories.find((x) => x.id === cid);
    if (!c) return cid;
    return lang === "ml" && c.name_ml ? c.name_ml : c.name;
  };

  const detailSchema = useMemo(() => {
    if (!data?.message) return null;
    const m = data.message;
    return [
      {
        "@type": "SocialMediaPosting",
        "@id": `https://thenextcm.com/m/${m.id}`,
        "url": `https://thenextcm.com/m/${m.id}`,
        "datePublished": m.created_at,
        "articleBody": m.message,
        "headline": m.message.slice(0, 110),
        "inLanguage": ["en", "ml"],
        "interactionStatistic": {
          "@type": "InteractionCounter",
          "interactionType": { "@type": "LikeAction" },
          "userInteractionCount": m.upvotes || 0
        },
        "about": { "@type": "Thing", "name": categoryName(m.category) },
        "isPartOf": { "@id": "https://thenextcm.com/#website" },
        "publisher": { "@id": "https://thenextcm.com/#org" }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thenextcm.com/" },
          { "@type": "ListItem", "position": 2, "name": "Message", "item": `https://thenextcm.com/m/${m.id}` }
        ]
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, categories, lang]);

  const seoTitle = data?.message
    ? `"${data.message.message.slice(0, 80)}${data.message.message.length > 80 ? '…' : ''}" | Kerala Speaks`
    : (lang === "ml" ? "സന്ദേശം — കേരളം പറയുന്നു" : "Citizen message — Kerala Speaks");
  const seoDesc = data?.message
    ? `A Kerala citizen on ${categoryName(data.message.category)}: "${data.message.message.slice(0, 160)}${data.message.message.length > 160 ? '…' : ''}" — upvote on Kerala Speaks if you agree.`
    : (lang === "ml" ? "ഒരു മലയാളിയുടെ പൗര സന്ദേശം." : "A Kerala citizen's public message to the new Chief Minister.");
  const noindex = data ? data.indexable === false : true;

  return (
    <div className="App min-h-screen" data-testid="message-detail-page">
      <SeoHead
        title={seoTitle}
        description={seoDesc}
        canonical={`/m/${id}`}
        ogType="article"
        ogImage={`/api/og/${id}.png`}
        schema={detailSchema || undefined}
        noindex={noindex}
      />
      <section className="max-w-3xl mx-auto px-5 sm:px-6 md:px-12 pt-8 md:pt-12 pb-20">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase font-extrabold tracking-[0.25em] mb-6"
          data-testid="detail-back-link"
        >
          <ArrowLeft size={14} strokeWidth={3} /> {t("detail_back")}
        </Link>

        {loading && (
          <p className="font-display uppercase text-2xl" data-testid="detail-loading">
            {t("loading")}
          </p>
        )}

        {error && !loading && (
          <div
            className="brutal-card p-6"
            style={{ background: "#FFE4E6" }}
            data-testid="detail-not-found"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle size={22} strokeWidth={3} style={{ color: "#E3000F" }} />
              <div>
                <h2 className="font-display uppercase leading-none" style={{ fontSize: "clamp(1.25rem, 3vw, 1.75rem)" }}>
                  {t("detail_not_found_heading")}
                </h2>
                <p className="text-sm md:text-base font-medium mt-2">{t("detail_not_found_body")}</p>
                <button
                  type="button"
                  onClick={() => navigate("/")}
                  className="brutal-btn mt-4"
                  style={{ background: "#0A0A0A", color: "#FF9933", borderColor: "#0A0A0A" }}
                  data-testid="detail-go-home"
                >
                  {t("detail_go_home")} →
                </button>
              </div>
            </div>
          </div>
        )}

        {data?.message && !loading && !error && (
          <>
            {/* Hero — friend's invitation context */}
            <p
              className="text-xs uppercase font-bold tracking-[0.4em] text-neutral-600 mb-4"
              data-testid="detail-eyebrow"
            >
              ★ {t("detail_eyebrow")} ★
            </p>
            <h1
              className="font-display uppercase leading-[0.85] tracking-tighter"
              style={{ fontSize: "clamp(2rem, 6vw, 4rem)" }}
              data-testid="detail-title"
            >
              {t("detail_title")}
            </h1>
            <p className="text-sm md:text-base font-medium mt-4 mb-8 max-w-2xl text-neutral-700" data-testid="detail-sub">
              {t("detail_sub")}
            </p>

            {/* THE message — highlighted */}
            <div
              className="brutal-card p-1 mb-10"
              style={{ background: "#FF9933" }}
              data-testid="detail-message-wrap"
            >
              <div className="bg-[#FDFBF7] p-4 md:p-5">
                <MessageCard
                  msg={data.message}
                  categoryName={categoryName(data.message.category)}
                  initiallyUpvoted={upvotedSet.has(data.message.id)}
                  onUpvoted={(mid) => setUpvotedSet((prev) => new Set(prev).add(mid))}
                />
              </div>
            </div>

            {/* Related */}
            {data.related?.length > 0 && (
              <>
                <h3
                  className="font-display uppercase leading-none mb-4"
                  style={{ fontSize: "clamp(1.25rem, 3vw, 1.75rem)" }}
                  data-testid="detail-related-heading"
                >
                  ★ {t("detail_related_heading")} {categoryName(data.message.category).toUpperCase()} ★
                </h3>
                <div className="space-y-4 mb-10" data-testid="detail-related-list">
                  {data.related.map((m) => (
                    <MessageCard
                      key={m.id}
                      msg={m}
                      categoryName={categoryName(m.category)}
                      initiallyUpvoted={upvotedSet.has(m.id)}
                      onUpvoted={(mid) => setUpvotedSet((prev) => new Set(prev).add(mid))}
                    />
                  ))}
                </div>
              </>
            )}

            {/* CTA */}
            <div className="brutal-card p-5 md:p-7 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid="detail-cta">
              <div className="max-w-xl">
                <p className="text-xs uppercase font-bold tracking-[0.3em] text-neutral-600">
                  ★ {t("detail_cta_eyebrow")} ★
                </p>
                <h3
                  className="font-display uppercase leading-none mt-2"
                  style={{ fontSize: "clamp(1.5rem, 3vw, 2rem)" }}
                >
                  {t("detail_cta_title")}
                </h3>
                <p className="text-sm md:text-base font-medium mt-2 text-neutral-700">
                  {t("detail_cta_sub")}
                </p>
              </div>
              <Link
                to="/"
                className="brutal-btn shrink-0"
                style={{ background: "#0A0A0A", color: "#FF9933", borderColor: "#0A0A0A" }}
                onClick={() => Analytics.congratulateClick(data?.message?.id)}
                data-testid="detail-cta-btn"
              >
                <MessageCircle size={18} strokeWidth={3} className="mr-2" />
                {t("detail_cta_button")} →
              </Link>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default MessageDetail;
